/**
 * Japanese, as far as a keyboard can take it without a dictionary: kana.
 *
 * ── What this does and does not claim ───────────────────────────────────────
 * Type `nihongo` and get `にほんご`. That is the whole feature, and it stops
 * exactly where a real Japanese IME's second half begins — the conversion from
 * kana to 日本語, which needs a dictionary, a candidate list and somebody
 * choosing between 日本語 and にほんご every few words.
 *
 * Stopping here is a decision, not an unfinished job. Two reasons, and the
 * second is the one that settles it:
 *
 *   • Kana is correct, readable Japanese. A sentence written entirely in kana
 *     is what Japanese children write and what every Japanese reader can read.
 *     It is not broken text; it is plain text.
 *   • This box feeds a translation model, and a model reads kana perfectly
 *     well. The conversion to kanji would be work done for a reader who is not
 *     there — the person typing wants a translation, not a finished document.
 *
 * Chinese gets the full treatment (lib/pinyinIme.ts) because it has no choice:
 * there is no kana for Chinese, no readable intermediate form, and `zhongguo`
 * is not Chinese in any sense. Japanese has one, so Japanese uses it.
 *
 * ── How it composes ─────────────────────────────────────────────────────────
 * The pending romaji lives IN THE TEXTAREA, as Latin letters, and is replaced
 * the moment it resolves. So `k` shows `k`, `ky` shows `ky`, `kya` becomes
 * `きゃ` — the reader always sees what the keyboard is holding, which the
 * alternative (a hidden buffer) cannot show them.
 */

import type { ImeEdit } from './keyboards';

/**
 * Romaji → kana, longest first at lookup time.
 *
 * Hepburn plus the Kunrei spellings that people actually type (`si`, `tu`,
 * `hu`, `zi`), because a keyboard that rejects `sinbun` is a keyboard that
 * argues with its user about romanisation systems.
 */
const KANA: Record<string, string> = {
  a: 'あ', i: 'い', u: 'う', e: 'え', o: 'お',
  ka: 'か', ki: 'き', ku: 'く', ke: 'け', ko: 'こ',
  ga: 'が', gi: 'ぎ', gu: 'ぐ', ge: 'げ', go: 'ご',
  sa: 'さ', si: 'し', shi: 'し', su: 'す', se: 'せ', so: 'そ',
  za: 'ざ', zi: 'じ', ji: 'じ', zu: 'ず', ze: 'ぜ', zo: 'ぞ',
  ta: 'た', ti: 'ち', chi: 'ち', tu: 'つ', tsu: 'つ', te: 'て', to: 'と',
  da: 'だ', di: 'ぢ', du: 'づ', de: 'で', do: 'ど',
  na: 'な', ni: 'に', nu: 'ぬ', ne: 'ね', no: 'の',
  ha: 'は', hi: 'ひ', hu: 'ふ', fu: 'ふ', he: 'へ', ho: 'ほ',
  ba: 'ば', bi: 'び', bu: 'ぶ', be: 'べ', bo: 'ぼ',
  pa: 'ぱ', pi: 'ぴ', pu: 'ぷ', pe: 'ぺ', po: 'ぽ',
  ma: 'ま', mi: 'み', mu: 'む', me: 'め', mo: 'も',
  ya: 'や', yu: 'ゆ', yo: 'よ',
  ra: 'ら', ri: 'り', ru: 'る', re: 'れ', ro: 'ろ',
  wa: 'わ', wo: 'を', wi: 'ゐ', we: 'ゑ',
  nn: 'ん', "n'": 'ん',
  vu: 'ゔ',
  // Small kana, reachable on their own with a leading x/l as every IME allows.
  xa: 'ぁ', xi: 'ぃ', xu: 'ぅ', xe: 'ぇ', xo: 'ぉ',
  la: 'ぁ', li: 'ぃ', lu: 'ぅ', le: 'ぇ', lo: 'ぉ',
  xya: 'ゃ', xyu: 'ゅ', xyo: 'ょ', xtu: 'っ', ltu: 'っ', xtsu: 'っ',
  xwa: 'ゎ', xka: 'ヵ', xke: 'ヶ',
  // Yōon.
  kya: 'きゃ', kyu: 'きゅ', kyo: 'きょ', kye: 'きぇ',
  gya: 'ぎゃ', gyu: 'ぎゅ', gyo: 'ぎょ',
  sha: 'しゃ', shu: 'しゅ', sho: 'しょ', she: 'しぇ',
  sya: 'しゃ', syu: 'しゅ', syo: 'しょ',
  ja: 'じゃ', ju: 'じゅ', jo: 'じょ', je: 'じぇ',
  jya: 'じゃ', jyu: 'じゅ', jyo: 'じょ',
  zya: 'じゃ', zyu: 'じゅ', zyo: 'じょ',
  cha: 'ちゃ', chu: 'ちゅ', cho: 'ちょ', che: 'ちぇ',
  tya: 'ちゃ', tyu: 'ちゅ', tyo: 'ちょ',
  nya: 'にゃ', nyu: 'にゅ', nyo: 'にょ',
  hya: 'ひゃ', hyu: 'ひゅ', hyo: 'ひょ',
  bya: 'びゃ', byu: 'びゅ', byo: 'びょ',
  pya: 'ぴゃ', pyu: 'ぴゅ', pyo: 'ぴょ',
  mya: 'みゃ', myu: 'みゅ', myo: 'みょ',
  rya: 'りゃ', ryu: 'りゅ', ryo: 'りょ',
  fa: 'ふぁ', fi: 'ふぃ', fe: 'ふぇ', fo: 'ふぉ',
  va: 'ヴぁ', vi: 'ヴぃ', ve: 'ヴぇ', vo: 'ヴぉ',
  tsa: 'つぁ', tsi: 'つぃ', tse: 'つぇ', tso: 'つぉ',
  dya: 'ぢゃ', dyu: 'ぢゅ', dyo: 'ぢょ',
  thi: 'てぃ', dhi: 'でぃ', twu: 'とぅ', dwu: 'どぅ',
  wha: 'うぁ', whi: 'うぃ', whe: 'うぇ', who: 'うぉ',
};

/** Punctuation that is different in Japanese and that nobody wants to hunt for. */
const PUNCT: Record<string, string> = {
  '.': '。', ',': '、', '/': '・', '[': '「', ']': '」', '-': 'ー', '!': '！', '?': '？',
};

const MAX_ROMAJI = 4;
const VOWELS = 'aiueo';

/**
 * The longest romaji prefix of `buf` that is a kana, if any.
 *
 * Longest FIRST, which is the whole reason this is a loop rather than a
 * lookup: `kya` and `ka` are both in the table, and matching the short one
 * first would turn every yōon into two syllables.
 */
function longest(buf: string): { romaji: string; kana: string } | null {
  for (let n = Math.min(MAX_ROMAJI, buf.length); n > 0; n -= 1) {
    const head = buf.slice(0, n);
    const kana = KANA[head];
    if (kana) return { romaji: head, kana };
  }
  return null;
}

/** Could `buf` still grow into a kana? `ky` can; `kx` cannot. */
function reachable(buf: string): boolean {
  // `nn` is held for one key rather than resolved — see `typeRomaji`.
  if (buf === 'nn') return true;
  if (buf.length >= MAX_ROMAJI) return false;
  for (const key of Object.keys(KANA)) if (key.startsWith(buf)) return true;
  return false;
}

export interface RomajiPress {
  /** The edit to apply — always replaces the pending Latin with what it became. */
  edit: ImeEdit;
  /** The Latin still being held, which is also what is now on screen. */
  pending: string;
}

/**
 * One keystroke against the pending romaji.
 *
 * Three rules beyond the table, all of them things a Japanese typist expects
 * and none of them derivable from it:
 *
 *   • A DOUBLED CONSONANT is っ. `kitte` is きって, not きて — so a consonant
 *     that repeats itself emits っ and starts again.
 *   • A LONE `n` before another consonant is ん. `kanji` is かんじ. Without
 *     this the `n` sits in the buffer waiting for a vowel that never comes,
 *     and the reader watches their `n` disappear into the next syllable.
 *   • `nn` WAITS ONE KEY, because it means two different things depending on
 *     what follows — see the comment on it below.
 *   • AN IMPOSSIBLE BUFFER FLUSHES. `x` after `k` cannot become anything, so
 *     `k` is given up as a literal `k` rather than held forever. A buffer that
 *     never empties is a keyboard that eats letters.
 */
export function typeRomaji(pending: string, key: string): RomajiPress {
  const back = pending.length;
  const done = (out: string, next: string): RomajiPress => ({
    edit: { back, insert: out + next },
    pending: next,
  });

  if (PUNCT[key]) return done(PUNCT[key], '');

  const ch = key.toLowerCase();
  if (!/^[a-z']$/.test(ch)) {
    // Anything else — a digit, a space, a bracket — ends the syllable and goes
    // in as itself. The pending Latin stays as the Latin it already is: it is
    // what the reader typed, and guessing a kana for it would be worse.
    return done(pending + key, '');
  }

  let buf = pending;
  let out = '';

  /*
   * `nn` IS HELD, NOT RESOLVED — and this is the one rule that cannot be
   * decided from the keystroke in hand.
   *
   * `nn` on its own is ん: it is how every Japanese typist forces one. But
   * `konnichiwa` is こんにちわ, which means the same two keys there are ん
   * followed by the start of に. Resolving `nn` the moment it arrives gets one
   * of those right and the other wrong — the first version shipped こんいちは.
   *
   * So `nn` waits one key. A vowel after it takes the second `n` with it (ん +
   * に); a consonant does not (ん, then the consonant); and nothing after it at
   * all is ん, via the table. One key of lookahead, which is the least a
   * correct answer needs.
   */
  if (buf === 'nn') {
    out += 'ん';
    buf = VOWELS.includes(ch) || ch === 'y' ? 'n' : '';
  } else if (buf === 'n' && ch === 'n') {
    return done(out, 'nn');
  } else if (buf === 'n' && ch !== 'y' && !VOWELS.includes(ch) && ch !== "'") {
    // ん before a consonant: `kanji` is かんじ. Without this the `n` sits in
    // the buffer waiting for a vowel that never comes, and the reader watches
    // it disappear into the next syllable.
    out += 'ん';
    buf = '';
  }
  // っ from a doubled consonant. `n` is excluded because doubled `n` is the
  // rule above, not this one.
  if (buf.length === 1 && buf === ch && !VOWELS.includes(ch) && ch !== 'n') {
    out += 'っ';
    buf = '';
  }

  buf += ch;

  const hit = longest(buf);
  if (hit && hit.romaji === buf) return done(out + hit.kana, '');
  if (reachable(buf)) return done(out, buf);

  // Not a kana and never going to be. Take the longest kana off the front if
  // there is one, and carry the rest.
  if (hit) {
    const rest = buf.slice(hit.romaji.length);
    return reachable(rest) ? done(out + hit.kana, rest) : done(out + hit.kana + rest, '');
  }
  return done(out + buf, '');
}

/**
 * The pending romaji, finished off — pressing space, Enter or translating.
 *
 * A lone `n` becomes ん, which is the one case where giving up on the buffer
 * has a right answer. Everything else goes in as the Latin it is.
 */
export function flushRomaji(pending: string): string {
  if (!pending) return '';
  if (pending === 'n') return 'ん';
  const hit = longest(pending);
  return hit && hit.romaji === pending ? hit.kana : pending;
}

/**
 * The on-screen board: the kana, in the order every Japanese chart uses.
 *
 * NOT a picture of the QWERTY keys, and that is the difference from every
 * other keyboard in this product. The others teach a mapping — where ц lives
 * on a Russian board — because there is one to learn. Romaji has no mapping to
 * teach: the reader already knows that `ka` is か, or is about to. What they
 * want from a panel is the chart, and the chart is the gojūon.
 */
export const GOJUON: { kana: string; romaji: string }[][] = [
  ['a', 'i', 'u', 'e', 'o'],
  ['ka', 'ki', 'ku', 'ke', 'ko'],
  ['sa', 'shi', 'su', 'se', 'so'],
  ['ta', 'chi', 'tsu', 'te', 'to'],
  ['na', 'ni', 'nu', 'ne', 'no'],
  ['ha', 'hi', 'fu', 'he', 'ho'],
  ['ma', 'mi', 'mu', 'me', 'mo'],
  ['ya', '', 'yu', '', 'yo'],
  ['ra', 'ri', 'ru', 're', 'ro'],
  ['wa', '', 'nn', '', 'wo'],
].map((row) => row.map((romaji) => ({ romaji, kana: romaji ? KANA[romaji] ?? '' : '' })));

/** The voiced and half-voiced rows, which the chart above deliberately leaves out. */
export const DAKUON: { kana: string; romaji: string }[][] = [
  ['ga', 'gi', 'gu', 'ge', 'go'],
  ['za', 'ji', 'zu', 'ze', 'zo'],
  ['da', 'di', 'du', 'de', 'do'],
  ['ba', 'bi', 'bu', 'be', 'bo'],
  ['pa', 'pi', 'pu', 'pe', 'po'],
].map((row) => row.map((romaji) => ({ romaji, kana: KANA[romaji] ?? '' })));
